import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { User, UserRole } from '../types/user';
import { setUserRole, deleteUser } from '../utils/authUtils';

const Container = styled.div`
  max-width: 800px;
  margin: 50px auto;
  padding: 20px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px;
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
`;

const Td = styled.td`
  padding: 12px;
  border-bottom: 1px solid #dee2e6;
`;

const Select = styled.select`
  padding: 6px;
  border-radius: 4px;
  border: 1px solid #ddd;
`;

const Message = styled.div<{ isError?: boolean }>`
  color: ${props => props.isError ? '#dc3545' : '#28a745'};
  margin-top: 10px;
  padding: 10px;
  border-radius: 4px;
  background-color: ${props => props.isError ? '#f8d7da' : '#d4edda'};
`;

const Button = styled.button`
  padding: 6px 12px;
  border-radius: 4px;
  border: none;
  background-color: #dc3545;
  color: white;
  cursor: pointer;
  margin-left: 10px;

  &:hover {
    background-color: #c82333;
  }
`;

const ActionCell = styled(Td)`
  display: flex;
  align-items: center;
`;

const AdminSettings: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [message, setMessage] = useState<{ text: string; isError: boolean } | null>(null);

  useEffect(() => {
    // 在实际应用中，这里应该从后端 API 获取用户列表
    const storedUsers = localStorage.getItem('users');
    if (storedUsers) {
      setUsers(JSON.parse(storedUsers));
    }
  }, []);

  const handleRoleChange = async (userId: string, newRole: UserRole) => {
    try {
      await setUserRole(userId, newRole);
      setMessage({ text: '用户角色更新成功', isError: false });
      
      // 更新本地状态
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId ? { ...user, role: newRole } : user
        )
      );
    } catch (error) {
      setMessage({ 
        text: error instanceof Error ? error.message : '更新角色失败', 
        isError: true 
      });
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('确定要删除这个用户吗？此操作不可撤销。')) {
      return;
    }

    try {
      await deleteUser(userId);
      setMessage({ text: '用户删除成功', isError: false });
      
      // 更新本地状态
      setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
    } catch (error) {
      setMessage({ 
        text: error instanceof Error ? error.message : '删除用户失败', 
        isError: true 
      });
    }
  };

  return (
    <Container>
      <h2>管理员设置</h2>
      {message && (
        <Message isError={message.isError}>
          {message.text}
        </Message>
      )}
      <Table>
        <thead>
          <tr>
            <Th>用户名</Th>
            <Th>邮箱</Th>
            <Th>当前角色</Th>
            <Th>操作</Th>
          </tr>
        </thead>
        <tbody>
          {users.map(user => (
            <tr key={user.id}>
              <Td>{user.username}</Td>
              <Td>{user.email}</Td>
              <Td>{user.role}</Td>
              <ActionCell>
                <Select
                  value={user.role}
                  onChange={(e) => handleRoleChange(user.id, e.target.value as UserRole)}
                >
                  <option value={UserRole.USER}>普通用户</option>
                  <option value={UserRole.ADMIN}>管理员</option>
                  <option value={UserRole.SUPER_ADMIN}>超级管理员</option>
                </Select>
                <Button onClick={() => handleDeleteUser(user.id)}>
                  删除
                </Button>
              </ActionCell>
            </tr>
          ))}
        </tbody>
      </Table>
    </Container>
  );
};

export default AdminSettings; 